<template>
  <div class="min-h-screen bg-linear-to-b from-bubba-blue-light/30 to-background">
    <!-- Header -->
    <header class="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-40">
      <div class="container flex h-16 items-center justify-between">
        <NuxtLink :to="$localeRoute('/vet')" class="flex items-center gap-2">
          <Icon name="tabler:arrow-left" size="20" class="text-muted-foreground" />
          <span class="font-bold text-xl text-bubba-blue">Bubba</span>
        </NuxtLink>
      </div>
    </header>

    <!-- Main Content -->
    <main class="container py-8 md:py-12">
      <!-- Success State -->
      <div v-if="isComplete" class="w-full max-w-md mx-auto text-center space-y-6">
        <div class="w-20 h-20 mx-auto rounded-full bg-muted flex items-center justify-center">
          <Icon name="tabler:check" size="40" class="text-foreground" />
        </div>
        <div class="space-y-2">
          <h1 class="text-2xl font-bold text-foreground">
            {{ $t('vet.signup.success.title') }}
          </h1>
          <p class="text-muted-foreground">
            {{ $t('vet.signup.success.description', { petName: completedData?.petName || 'your pet' }) }}
          </p>
          <p class="text-sm text-muted-foreground">
            {{ $t('vet.signup.success.nextSteps') }}
          </p>
        </div>
        <div class="flex flex-col sm:flex-row gap-3 justify-center pt-4">
          <UiButton class="bg-bubba-blue hover:bg-bubba-blue/90" @click="navigateTo($localeRoute('/vet'))">
            {{ $t('vet.signup.success.goToConsultation') }}
          </UiButton>
          <UiButton variant="outline" @click="navigateTo($localeRoute('/account'))">
            {{ $t('vet.signup.success.goToProfile') }}
          </UiButton>
        </div>
      </div>

      <!-- Signup Wizard -->
      <template v-else>
        <div class="text-center mb-8">
          <h1 class="text-3xl font-bold text-foreground">
            {{ $t('vet.signup.title') }}
          </h1>
          <p class="text-muted-foreground mt-2">
            {{ $t('vet.signup.subtitle') }}
          </p>
        </div>

        <SignupWizard ref="wizardRef" @complete="handleComplete" />
      </template>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { api } from '@convex/_generated/api';
import { definePageMeta, navigateTo, useI18n, useNuxtApp, useSeoMeta } from '#imports';
import SignupWizard from '#components/vet/SignupWizard.vue';
import { useConvexMutation } from '#composables/useConvex';
import type { SignupFormData } from '#composables/vet/useVetSignupWizard';
import { useAuthClient } from '#composables/useAuthClient';
import { primeSessionCache } from '#bubba/shared/utils/sessionCache';
import { createLogger } from '#bubba/shared/utils/logger';

const log = createLogger('VetSignup');

definePageMeta({
  layout: false
});

const { $t, $ts, $localeRoute } = useI18n();

useSeoMeta({
  title: () => $ts('vet.signup.title') + ' | Bubba',
  description: () => $ts('vet.signup.seo.description'),
});

const wizardRef = ref<InstanceType<typeof SignupWizard> | null>(null);
const isComplete = ref(false);
const completedData = ref<SignupFormData | null>(null);

async function handleComplete(data: SignupFormData) {
  // Get auth client and convex mutation only on client-side
  const authClient = useAuthClient();
  const createPet = useConvexMutation(api.users.pets.createPet);
  const { $refreshConvexAuth } = useNuxtApp();

  try {
    // 1. Create user account
    const signupResult = await authClient.signUp.email({
      name: data.ownerName,
      email: data.email,
      password: data.password,
    });

    if (signupResult.error) {
      throw new Error(signupResult.error.message || $ts('vet.signup.errors.generic'));
    }

    // 2. Sign in the user immediately (for non-email-verified flow)
    const signinResult = await authClient.signIn.email({
      email: data.email,
      password: data.password,
    });

    if (signinResult.error) {
      // Account created but couldn't sign in - still show success
      log.warn('Auto-signin failed:', signinResult.error);
    } else {
      // Prime session cache and refresh Convex auth so the createPet
      // mutation has an authenticated token immediately.
      primeSessionCache(signinResult);
      if (typeof $refreshConvexAuth === 'function') {
        $refreshConvexAuth();
      }
    }

    // 3. Create the pet (requires authenticated session)
    try {
      await createPet({
        species: data.species as "dog" | "cat" | "bird" | "rabbit" | "hamster" | "guinea_pig" | "fish" | "turtle" | "snake" | "lizard" | "ferret" | "horse" | "other",
        breedSlug: data.breedSlug,
        name: data.petName,
        age: data.petAge,
        weight: data.petWeight,
        problemDescription: data.problemDescription || undefined,
      });
    } catch (petError) {
      log.error('Failed to create pet:', petError);
      // Still show success since account was created
    }

    // 4. Show success state
    completedData.value = data;
    isComplete.value = true;

  } catch (error: unknown) {
    log.error('Signup error:', error);

    // Try to get the step component to show error
    // For now, we'll use an alert
    const message = error instanceof Error ? error.message : $ts('vet.signup.errors.generic');
    alert(message);
  }
}
</script>
